import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getProvider } from '@galapagos/observability';
import {
  conversionAttributionCapturedAtNow,
  evaluateConversionAttributionFromSearchParams,
} from '@galapagos/shared';
import { persistConversionAttribution } from '../services/attribution-store';

/**
 * Root-owned landing capture (ADR-132): reads the attribution params (the
 * Iterable pair, the UTMs, the experiment arm, the link ref) off the current
 * URL on any route and persists a record when at least one value is valid.
 * Mounted once under the analytics provider inside a Suspense boundary
 * (Next's `useSearchParams` contract for a statically exported page); no
 * feature screen calls this.
 *
 * Re-evaluated on every distinct URL (pathname plus query string): the
 * component stays mounted for the life of the SPA, so a later landing on the
 * SAME values must still count as a fresh touch and refresh `capturedAt`, or
 * a member who re-enters from the same link near the end of the 24-hour
 * window would convert with a stale, soon-expired record. An in-flow
 * navigation that carries no attribution param is not an attempt and leaves
 * the earlier capture untouched.
 *
 * The operational signal carries bounded enums only. `attribution_source`
 * rather than `source`: the latter is a Datadog-reserved log key.
 */
export function useConversionAttributionCapture(): void {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();

  useEffect(() => {
    const evaluation = evaluateConversionAttributionFromSearchParams(
      new URLSearchParams(search),
      conversionAttributionCapturedAtNow(),
    );
    if (!evaluation.attempted) return;
    const captured = evaluation.record !== null;
    // Clean only when everything present was kept; a dropped value is worth a
    // look, though the reason is all the signal ever says.
    getProvider().logs.log(
      captured && evaluation.reason === 'none' ? 'info' : 'warn',
      'galapagos.attribution.captured',
      {
        attribution_source: 'url',
        outcome: captured ? 'captured' : 'rejected',
        reason: evaluation.reason,
      },
    );
    if (evaluation.record) persistConversionAttribution(evaluation.record);
    // `pathname` and `search` together identify a landing.
  }, [pathname, search]);
}
